// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useState } from 'react';
import { ActivityIndicator, Pressable, StyleSheet, Text, View } from 'react-native';

import { useI18n } from '@/i18n';
import appColors from '@/theme/colors';

import {
  requestCourseAnalysis,
  useCourseAnalysisStatus,
  type AnalysisState,
  type AnalysisRun,
} from './useCourseAnalysisStatus';

/**
 * Admin diagnostics for one course's imagery analysis.
 *
 * The state this reports used to be spread across a Pulumi config value, a row in
 * `geo_courses` and a log line written once at service startup — so answering "why is this
 * course blank?" meant an Elasticsearch query over a wide enough window against a service
 * that had not restarted since, and every one of those has a silent way to look like
 * "nothing is wrong". The whole point of this panel is that a blank course now explains
 * itself, and the fix for two of the five states is the button at the bottom of it.
 */

/** Feature kinds this build has a translated name for. */
const TRANSLATED_KINDS = ['tee', 'green', 'bunker', 'water', 'fairway', 'trees'];

/** How each state reads at a glance. Only `analysed` is a finished state. */
const STATE_TONE: Record<AnalysisState, 'good' | 'warn' | 'bad'> = {
  analysed: 'good',
  pending: 'warn',
  neverRequested: 'warn',
  abandoned: 'bad',
  disabled: 'bad',
};

export type CourseAnalysisStatusPanelProps = {
  apiBaseUrl: string;
  token: string | null | undefined;
  courseId: string;
};

export function CourseAnalysisStatusPanel({
  apiBaseUrl,
  token,
  courseId,
}: CourseAnalysisStatusPanelProps) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [runState, setRunState] = useState<'idle' | 'running' | 'queued' | 'unavailable' | 'failed'>(
    'idle'
  );
  const { status, loading, error, refresh } = useCourseAnalysisStatus(
    apiBaseUrl,
    token,
    courseId,
    open
  );

  const run = async () => {
    if (runState === 'running') return;
    setRunState('running');
    const outcome = await requestCourseAnalysis(apiBaseUrl, token, courseId);
    setRunState(outcome);
    // The loop claims the course on its own schedule, so this shows it queued rather than
    // finished. Re-reading now is what makes the requested-at timestamp move on screen.
    if (outcome === 'queued') refresh();
  };

  if (!open) {
    return (
      <Pressable
        accessibilityRole="button"
        testID="course-analysis-status-open"
        style={styles.openButton}
        onPress={() => setOpen(true)}
      >
        <Text style={styles.openButtonText}>{t('courseAnalysis.status.open')}</Text>
      </Pressable>
    );
  }

  return (
    <View style={styles.card} testID="course-analysis-status">
      <View style={styles.headerRow}>
        <Text style={styles.title}>{t('courseAnalysis.status.title')}</Text>
        <Pressable
          accessibilityRole="button"
          testID="course-analysis-status-close"
          onPress={() => setOpen(false)}
        >
          <Text style={styles.close}>{t('menu.close')}</Text>
        </Pressable>
      </View>

      {loading ? (
        <ActivityIndicator color={appColors.accent.primary} testID="course-analysis-status-loading" />
      ) : error === 'forbidden' ? (
        <Text style={styles.notice}>{t('courseAnalysis.adminOnly')}</Text>
      ) : error === 'unavailable' ? (
        <Text style={styles.notice} testID="course-analysis-status-endpoint-missing">
          {t('courseAnalysis.status.endpointMissing')}
        </Text>
      ) : error || !status ? (
        <Text style={styles.notice} testID="course-analysis-status-failed">
          {t('courseAnalysis.status.failed')}
        </Text>
      ) : (
        <>
          <View style={styles.stateRow}>
            <View style={[styles.badge, toneStyle(STATE_TONE[status.state])]}>
              <Text style={styles.badgeText} testID="course-analysis-status-state">
                {t(`courseAnalysis.status.state.${status.state}`)}
              </Text>
            </View>
            {status.attempts > 0 ? (
              <Text style={styles.meta}>
                {t('courseAnalysis.status.attempts', {
                  attempts: status.attempts,
                  max: status.maxAttempts,
                })}
              </Text>
            ) : null}
          </View>

          {/* The explanation is the point of the panel: the state names the situation, this
              says what to do about it. */}
          <Text style={styles.explanation} testID="course-analysis-status-explanation">
            {t(`courseAnalysis.status.explain.${status.state}`)}
          </Text>

          {/* Worker mode: the analyser is a machine that may be switched off. Say so — a
              queued job with nobody listening looks identical to one about to start. */}
          {status.mode === 'worker' && status.workerOnline !== null ? (
            <View style={styles.queueBlock}>
              <Text style={styles.sectionTitle}>{t('courseAnalysis.status.worker')}</Text>
              <Text
                style={status.workerOnline ? styles.meta : styles.notice}
                testID="course-analysis-status-worker"
              >
                {status.workerOnline
                  ? t('courseAnalysis.status.workerOnline')
                  : t('courseAnalysis.status.workerOffline')}
              </Text>
              {status.workers.map((worker) => (
                <Text key={worker.workerId} style={styles.meta} testID="course-analysis-status-worker-row">
                  {t('courseAnalysis.status.workerRow', {
                    id: worker.workerId,
                    version: worker.version ?? t('courseAnalysis.status.workerVersionUnknown'),
                  })}
                  {worker.currentJobId ? ` · ${t('courseAnalysis.status.workerBusy')}` : ''}
                </Text>
              ))}
            </View>
          ) : null}

          {/* Estate-wide, not per-course: tells the admin whether "pending" means "waiting
              behind a busy analyser" or "the analyser isn't picking anything up at all". */}
          {status.state !== 'disabled' ? (
            <View style={styles.queueBlock}>
              <Text style={styles.sectionTitle}>{t('courseAnalysis.status.queueDepth')}</Text>
              <Text style={styles.meta} testID="course-analysis-status-queue-depth">
                {status.queueDepth === null
                  ? t('courseAnalysis.status.queueDepthUnknown')
                  : t('courseAnalysis.status.queueDepthCount', { count: status.queueDepth })}
              </Text>
            </View>
          ) : null}

          <View style={styles.timestamps}>
            <Timestamp label={t('courseAnalysis.status.requestedAt')} value={status.requestedAt} />
            <Timestamp label={t('courseAnalysis.status.analysedAt')} value={status.analysedAt} />
          </View>

          {/* The run history, newest first: each pass with its per-provider numbers. This is
              the difference between "found nothing" and "never answered", as data. */}
          <View style={styles.outcomeBlock}>
            <Text style={styles.sectionTitle}>{t('courseAnalysis.status.lastRun')}</Text>
            {status.runs.length === 0 ? (
              <Text style={styles.meta} testID="course-analysis-status-no-outcome">
                {t('courseAnalysis.status.neverRun')}
              </Text>
            ) : (
              status.runs.map((run, index) => (
                <View
                  key={run.id}
                  style={styles.runRow}
                  testID={index === 0 ? 'course-analysis-status-outcome' : undefined}
                >
                  <View style={styles.runHeader}>
                    <View style={[styles.badge, toneStyle(RUN_TONE[run.status])]}>
                      <Text style={styles.badgeText}>{t(`courseAnalysis.status.runStatus.${run.status}`)}</Text>
                    </View>
                    <Text style={styles.meta}>{formatWhen(run.startedAt)}</Text>
                    {run.durationSeconds !== null ? (
                      <Text
                        style={styles.meta}
                        testID={index === 0 ? 'course-analysis-status-duration' : undefined}
                      >
                        {formatDuration(t, run.durationSeconds)}
                      </Text>
                    ) : null}
                  </View>
                  {run.error ? <Text style={styles.outcome}>{run.error}</Text> : null}
                  {run.providers.map((provider) => (
                    <Text key={provider.provider} style={styles.outcome}>
                      {t('courseAnalysis.status.providerLine', {
                        provider: provider.provider,
                        written: provider.written,
                        replaced: provider.replaced,
                        skipped: provider.skippedStrongerSource,
                        fetched: provider.tilesFetched,
                        attempted: provider.tilesAttempted,
                      })}
                      {provider.error
                        ? ` · ${t('courseAnalysis.status.providerFailed', { error: provider.error })}`
                        : provider.partial
                          ? ` · ${t('courseAnalysis.status.providerPartial')}`
                          : ''}
                    </Text>
                  ))}
                </View>
              ))
            )}
          </View>

          <View style={styles.featureBlock}>
            <Text style={styles.sectionTitle}>{t('courseAnalysis.status.stored')}</Text>
            {status.features.length === 0 ? (
              <Text style={styles.meta} testID="course-analysis-status-no-features">
                {t('courseAnalysis.status.noFeatures')}
              </Text>
            ) : (
              status.features.map((feature) => (
                <View key={`${feature.kind}-${feature.source}`} style={styles.featureRow}>
                  <Text style={styles.featureKind}>
                    {TRANSLATED_KINDS.includes(feature.kind)
                      ? t(`courseAnalysis.kind.${feature.kind}`)
                      : // A kind this build has no word for is shown raw. `t` returns the key
                        // itself when it misses, and "courseAnalysis.kind.xyz" on screen is
                        // worse than "xyz" — which is at least the truth.
                        feature.kind}
                  </Text>
                  <Text style={styles.featureMeta}>
                    {t('courseAnalysis.status.featureMeta', {
                      count: feature.count,
                      source: feature.source,
                      polygons: feature.withPolygon,
                    })}
                  </Text>
                </View>
              ))
            )}
          </View>

          <Pressable
            accessibilityRole="button"
            testID="course-analysis-status-run"
            style={[styles.runButton, runState === 'running' && styles.runButtonBusy]}
            disabled={runState === 'running'}
            onPress={() => void run()}
          >
            <Text style={styles.runButtonText}>
              {t(
                runState === 'running'
                  ? 'courseAnalysis.status.running'
                  : 'courseAnalysis.status.run'
              )}
            </Text>
          </Pressable>

          {runState === 'queued' ? (
            <Text style={styles.queued} testID="course-analysis-status-queued">
              {t('courseAnalysis.status.queued')}
            </Text>
          ) : runState === 'unavailable' ? (
            <Text style={styles.notice} testID="course-analysis-status-unavailable">
              {t('courseAnalysis.status.explain.disabled')}
            </Text>
          ) : runState === 'failed' ? (
            <Text style={styles.notice}>{t('courseAnalysis.status.runFailed')}</Text>
          ) : null}
        </>
      )}
    </View>
  );
}

function Timestamp({ label, value }: { label: string; value: string | null }) {
  const { t } = useI18n();
  return (
    <View style={styles.timestamp}>
      <Text style={styles.timestampLabel}>{label}</Text>
      <Text style={styles.timestampValue}>
        {value ? formatWhen(value) : t('courseAnalysis.status.never')}
      </Text>
    </View>
  );
}

/** Local time, to the minute. Anything finer is noise on a page about days-old state. */
const formatWhen = (iso: string): string => {
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime()) ? iso : parsed.toLocaleString();
};

/** A pass runs seconds to tens of minutes, so minutes+seconds covers the real range. */
const formatDuration = (
  t: (key: string, params?: Record<string, string | number>) => string,
  totalSeconds: number
): string => {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return minutes === 0
    ? t('courseAnalysis.status.durationSeconds', { seconds })
    : t('courseAnalysis.status.durationMinutes', { minutes, seconds });
};

const RUN_TONE: Record<AnalysisRun['status'], 'good' | 'warn' | 'bad'> = {
  running: 'warn',
  dispatched: 'warn',
  succeeded: 'good',
  partial: 'warn',
  failed: 'bad',
};

const toneStyle = (tone: 'good' | 'warn' | 'bad') =>
  tone === 'good' ? styles.badgeGood : tone === 'warn' ? styles.badgeWarn : styles.badgeBad;

const styles = StyleSheet.create({
  openButton: {
    alignSelf: 'stretch',
    height: 44,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: appColors.accent.primary,
    backgroundColor: '#07200D',
    alignItems: 'center',
    justifyContent: 'center',
  },
  openButtonText: {
    color: appColors.accent.primary,
    fontWeight: '700',
    fontSize: 12,
    letterSpacing: 1.4,
    textTransform: 'uppercase',
  },
  card: {
    backgroundColor: '#07200D',
    borderRadius: 14,
    borderWidth: 1,
    borderColor: '#1A3A20',
    padding: 14,
    gap: 10,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  title: { color: appColors.text.primary, fontSize: 16, fontWeight: '800' },
  close: {
    color: appColors.text.secondary,
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: 1.2,
    textTransform: 'uppercase',
  },
  stateRow: { flexDirection: 'row', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
  badge: { paddingHorizontal: 10, paddingVertical: 4, borderRadius: 8 },
  badgeGood: { backgroundColor: '#1F5B2E' },
  badgeWarn: { backgroundColor: '#6B5A1E' },
  badgeBad: { backgroundColor: '#7A2E2E' },
  badgeText: {
    color: appColors.text.primary,
    fontSize: 12,
    fontWeight: '800',
    letterSpacing: 0.6,
    textTransform: 'uppercase',
  },
  explanation: { color: appColors.text.secondary, fontSize: 13, lineHeight: 18 },
  timestamps: { flexDirection: 'row', gap: 16, flexWrap: 'wrap' },
  timestamp: { gap: 2 },
  timestampLabel: {
    color: appColors.text.muted,
    fontSize: 10,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
  },
  timestampValue: { color: appColors.text.primary, fontSize: 13 },
  queueBlock: { gap: 2 },
  outcomeBlock: {
    gap: 4,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: '#1A3A20',
    paddingTop: 10,
  },
  sectionTitle: {
    color: appColors.text.muted,
    fontSize: 10,
    letterSpacing: 0.8,
    textTransform: 'uppercase',
  },
  outcome: { color: appColors.text.primary, fontSize: 13, lineHeight: 18 },
  runRow: { gap: 4, paddingVertical: 4 },
  runHeader: { flexDirection: 'row', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  featureBlock: {
    gap: 4,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: '#1A3A20',
    paddingTop: 10,
  },
  featureRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 8,
  },
  featureKind: { color: appColors.text.primary, fontSize: 13, fontWeight: '600' },
  featureMeta: { color: appColors.text.secondary, fontSize: 12, flexShrink: 1, textAlign: 'right' },
  meta: { color: appColors.text.muted, fontSize: 12 },
  notice: { color: appColors.status.warning, fontSize: 13, lineHeight: 18 },
  queued: { color: appColors.accent.primary, fontSize: 13 },
  runButton: {
    alignSelf: 'stretch',
    height: 44,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: appColors.accent.primary,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 2,
  },
  runButtonBusy: { opacity: 0.6 },
  runButtonText: {
    color: appColors.accent.primary,
    fontWeight: '700',
    fontSize: 12,
    letterSpacing: 1.2,
    textTransform: 'uppercase',
  },
});

export default CourseAnalysisStatusPanel;
